import { Drawer, Stack } from '@mantine/core';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Specimen,
  Patient,
  CodeableConcept,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useSpecimenForm } from '../hooks/useSpecimenForm';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual } from 'lodash';
import { SpecimenCreateEditForm } from './SpecimenCreateEditForm';
import { OrderBanner } from '../components/OrderBanner';

interface SpecimenCreateEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  specimen?: Specimen | null;
  patient: Patient | undefined;
  defaultSampleType?: CodeableConcept;
  serviceRequest?: ServiceRequest;
}

export function SpecimenCreateEditDrawer({
  opened,
  onClose,
  specimen,
  patient,
  defaultSampleType,
  serviceRequest,
}: SpecimenCreateEditDrawerProps): JSX.Element {
  const { t } = useTranslation('lims');
  const {
    form,
    loading,
    selectedPatient,
    selectedOrders,
    availableOrders,
    loadingOrders,
    handleSubmit,
    handlePatientChange,
    handleOrdersChange,
  } = useSpecimenForm({
    specimen,
    patientId: patient?.id,
    defaultSampleType,
    opened,
    onClose,
    serviceRequest,
  });

  const formActionsRef = useRef<FormActionButtonsRef>(null);

  const baselineRef = useRef<{
    values: any;
    orderIds: string[];
    selectedPatient: Patient | undefined;
  } | null>(null);

  // Track if we've taken the baseline for this open
  const [baselineSet, setBaselineSet] = useState(false);

  // take baseline AFTER orders load
  useEffect(() => {
    if (opened && !loadingOrders && !baselineSet) {
      baselineRef.current = {
        values: form.getValues(),
        orderIds: selectedOrders.map((o) => o.id || ''),
        selectedPatient: selectedPatient,
      };
      setBaselineSet(true);
    }
    if (!opened) {
      baselineRef.current = null;
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, loadingOrders, selectedOrders, selectedPatient]);

  const hasUnsavedChanges = (): boolean => {
    try {
      const current = {
        values: form.getValues(),
        orderIds: selectedOrders.map((o) => o.id || ''),
        selectedPatient: selectedPatient,
      };
      return !isEqual(current, baselineRef.current);
    } catch {
      return true;
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      form.setValues(baselineRef.current.values);
      handleOrdersChange(baselineRef.current.orderIds);
      handlePatientChange(baselineRef.current.selectedPatient);
    } else {
      handlePatientChange(undefined);
      form.reset();
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={
        specimen ? t('specimenModal.editTitle') : t('specimenModal.createTitle')
      }
      size="xl"
      position="right"
      offset={8}
      radius="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
      >
        <Stack gap="md">
          {serviceRequest && patient && defaultSampleType && (
            <OrderBanner
              serviceRequest={serviceRequest}
              patient={patient}
              specimenType={defaultSampleType}
            />
          )}

          <SpecimenCreateEditForm
            form={form}
            specimen={specimen}
            patient={patient}
            defaultSampleType={defaultSampleType}
            selectedPatient={selectedPatient}
            selectedOrders={selectedOrders}
            availableOrders={availableOrders}
            loadingOrders={loadingOrders}
            onPatientChange={handlePatientChange}
            onOrdersChange={handleOrdersChange}
            hideOrderSelection={!!serviceRequest && !specimen}
          />
          <FormActionButtons
            ref={formActionsRef}
            onDiscard={handleDiscardChanges}
            onClose={handleClose}
            hasUnsavedChanges={hasUnsavedChanges()}
            isLoading={loading}
            saveLabel={`${specimen ? t('specimenModal.update') : t('specimenModal.create')}`}
          />
        </Stack>
      </form>
    </Drawer>
  );
}
